
import React, { useEffect } from 'react';
import { Users, Award, Shield, CheckCircle, Target, Globe, Factory, Beaker } from 'lucide-react';

const About: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <Shield className="w-8 h-8 text-chemical-600" />,
      title: 'Quality Excellence',
      description: 'We maintain the highest standards of quality in all our products and processes.'
    },
    {
      icon: <Target className="w-8 h-8 text-chemical-600" />,
      title: 'Customer Focus',
      description: 'Our customers are at the heart of everything we do, ensuring satisfaction and trust.'
    },
    {
      icon: <Globe className="w-8 h-8 text-chemical-600" />,
      title: 'Global Standards',
      description: 'We adhere to international quality and safety standards in all our operations.'
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-chemical-600" />,
      title: 'Reliability',
      description: 'Consistent delivery of high-quality products that meet your specifications.'
    }
  ];

  const milestones = [
    {
      title: 'Company Founded',
      description: 'Tatvam Chemicals LLP was established with a vision to provide quality chemical solutions.'
    },
    {
      title: 'Product Expansion',
      description: 'Expanded product portfolio to include Liquid Bromine and Magnesium Chloride.'
    },
    {
      title: 'Quality Certification',
      description: 'Achieved various quality certifications and expanded market presence.'
    },
    {
      title: 'Modern Operations',
      description: 'Continued growth with modern manufacturing facilities and advanced technology.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-chemical-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-chemical-600/10 to-blue-600/10 dark:from-chemical-600/20 dark:to-blue-600/20"></div>
        <div className="container-custom section-padding relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              About <span className="text-chemical-600 dark:text-chemical-400">Tatvam Chemicals</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
              Rich experience in chemical manufacturing, providing high-quality solutions
              for industries worldwide. Our commitment to quality, innovation, and customer satisfaction
              has made us a trusted partner in the chemical industry.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="section-padding bg-white dark:bg-gray-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-300">
                <p>
                  Tatvam Chemicals LLP was established with a vision to become a leading provider of 
                  high-quality chemical solutions. With rich experience in the industry, we have grown from a 
                  small manufacturing unit to a trusted partner for industries across India.
                </p>
                <p>
                  Our journey began with a simple mission: to provide reliable, high-quality chemical 
                  products that meet the diverse needs of our customers. Today, we are proud to serve 
                  various industries including agriculture, pharmaceuticals, construction, and manufacturing.
                </p>
                <p>
                  With our state-of-the-art manufacturing facilities and dedicated team of professionals, 
                  we continue to innovate and expand our product portfolio while maintaining the highest 
                  standards of quality and safety.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-chemical-100 to-blue-100 dark:from-chemical-900 dark:to-blue-900 rounded-xl p-8">
              <div className="text-center">
                <Factory className="w-16 h-16 text-chemical-600 dark:text-chemical-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Vast Industry Experience
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Deep expertise in chemical manufacturing with proven track record of delivering quality solutions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Credentials */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Business Credentials
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Our commitment to quality and compliance is reflected in our certifications and credentials.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Company Name</h3>
              <p className="text-gray-600 dark:text-gray-300">Tatvam Chemicals LLP</p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">GST Number</h3>
              <p className="text-gray-600 dark:text-gray-300">24AARFT6743L1ZH</p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Experience</h3>
              <p className="text-gray-600 dark:text-gray-300">40+ Years</p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center border-l-4 border-chemical-600">
              <div className="w-12 h-12 bg-chemical-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Beaker className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Products</h3>
              <p className="text-gray-600 dark:text-gray-300">Chemical Solutions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white dark:bg-gray-900">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <Target className="w-8 h-8 text-chemical-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Our Vision</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                To become the most trusted and preferred chemical solutions provider, known for innovation, 
                reliability, and sustainable practices in the industry. We aim to be the go-to partner for 
                all chemical requirements, delivering excellence in every product and service.
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <Award className="w-8 h-8 text-chemical-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Our Mission</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                To provide high-quality chemical solutions that meet the diverse needs of our customers 
                while maintaining the highest standards of safety, quality, and environmental responsibility. 
                We are committed to continuous improvement and customer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              The principles that guide our business and relationships with customers, employees, and stakeholders.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center hover:shadow-lg transition-shadow duration-200">
                <div className="flex justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-white dark:bg-gray-900">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Key milestones in our journey of growth and excellence.
            </p>
          </div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-chemical-200 dark:bg-chemical-800"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-chemical-600 rounded-full border-4 border-white dark:border-gray-900"></div>
                  
                  {/* Content */}
                  <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                      <h3 className="text-xl font-semibold text-chemical-600 dark:text-chemical-400 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose Tatvam Chemicals?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              We deliver excellence in every product, backed by decades of experience and commitment to quality.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Quality Assurance</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Rigorous quality control processes ensure every product meets international standards.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Expert Team</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Experienced professionals provide technical support and guidance for optimal results.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Proven Track Record</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Years of successful operations and satisfied customers across industries.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Global Standards</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Products manufactured to meet international quality and safety standards.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Reliable Delivery</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Consistent and timely delivery of products to meet your production schedules.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-chemical-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Beaker className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Custom Solutions</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Tailored chemical solutions to meet your specific requirements and applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-chemical-600">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Partner with Us?
          </h2>
          <p className="text-xl text-chemical-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust Tatvam Chemicals for their chemical requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-chemical-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center leading-none">
              Contact Us
            </a>
            <a href="/products" className="border-2 border-white text-white hover:bg-white hover:text-chemical-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center leading-none">
              View Products
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 